import { useCallback, useEffect, useState } from "react";
import { AxiosRequest } from "_services/api";
import { toastErro, toastSucesso } from "components/Toast";
import { IImpressaoEtiqueta, StatusImpressao } from "../interfaces/IImpressaoEtiqueta";
import {
  buscarImpressoraPadrao,
  listarImpressoras,
  enviarZpl,
  ZebraDevice,
} from "_services/zebraBrowserPrint/zebraBrowserPrint.service";
import { gerarZplEtiquetaProcesso, DadosEtiquetaZpl } from "_services/zebraBrowserPrint/zplTemplate";

// const api = AxiosRequest.makeApi("http://localhost:6005/api/v2"); // TODO: retirar depois
const api = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);

export interface FiltrosImpressao {
  placa: string[];
  idProcesso: string[];
  identificadorMaquina: string;
  status: StatusImpressao | "";
  ordenarPor: "placa" | "criadoEm";
  ordenacao: "ASC" | "DESC";
  dataInicio: string;
  dataFim: string;
}

const filtrosIniciais: FiltrosImpressao = {
  placa: [],
  idProcesso: [],
  identificadorMaquina: "",
  status: "PENDENTE",
  ordenarPor: "placa",
  ordenacao: "DESC",
  dataInicio: "",
  dataFim: "",
};

export function useImpressaoEtiqueta() {
  const [registros, setRegistros] = useState<IImpressaoEtiqueta[]>([]);
  const [loading, setLoading] = useState(false);
  const [impressora, setImpressora] = useState<ZebraDevice | null>(null);
  const [erroImpressora, setErroImpressora] = useState<string | null>(null);
  const [impressoras, setImpressoras] = useState<ZebraDevice[]>([]);
  const [filtros, setFiltros] = useState<FiltrosImpressao>(filtrosIniciais);

  const verificarImpressora = useCallback(async () => {
    try {
      const device = await buscarImpressoraPadrao();
      setImpressora(device);
      setErroImpressora(null);
    } catch (error: any) {
      setImpressora(null);
      setErroImpressora(error?.message ?? "Impressora não encontrada");
    }
  }, []);

  const carregarImpressoras = useCallback(async () => {
    try {
      const lista = await listarImpressoras();
      setImpressoras(lista);
    } catch {
      setImpressoras([]);
    }
  }, []);

  const buscarRegistros = useCallback(async (f?: FiltrosImpressao) => {
    setLoading(true);
    const params = f ?? filtros;
    try {
      const query = new URLSearchParams();
      params.placa?.forEach((p) => query.append("placa", p));
      params.idProcesso?.forEach((p) => query.append("idProcesso", p));
      if (params.identificadorMaquina) query.set("identificadorMaquina", params.identificadorMaquina);
      if (params.status) query.set("status", params.status);
      query.set("ordenarPor", params.ordenarPor);
      query.set("ordenacao", params.ordenacao);
      if (params.dataInicio) query.set("dataInicio", params.dataInicio);
      if (params.dataFim) query.set("dataFim", params.dataFim);

      const { data } = await api.get<IImpressaoEtiqueta[]>(`/impressao-etiqueta?${query.toString()}`);
      setRegistros(data);
    } catch (error: any) {
      alert(`Erro ao buscar registros: ${error?.message ?? "Erro desconhecido"}`);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  const atualizarStatus = useCallback(
    async (idProcesso: string, status: StatusImpressao, mensagemErro?: string) => {
      await api.patch(`/impressao-etiqueta/${idProcesso}/status`, { status, mensagemErro });
    },
    []
  );

  const reimprimir = useCallback(
    async (idProcesso: string) => {
      try {
        await atualizarStatus(idProcesso, "IMPRIMINDO");
        const { data } = await api.get<DadosEtiquetaZpl>(
          `/impressao-etiqueta/etiqueta/${idProcesso}`
        );
        const zpl = gerarZplEtiquetaProcesso(data);
        await enviarZpl(zpl, impressora ?? undefined);
        await atualizarStatus(idProcesso, "IMPRESSO");
      } catch (error: any) {
        const msg = error?.response?.data?.message ?? error?.message ?? "Falha na reimpressão";
        toastErro(msg);
        await atualizarStatus(idProcesso, "ERRO", msg);
      } finally {
        await buscarRegistros();
      }
    },
    [atualizarStatus, impressora, buscarRegistros]
  );

  const imprimirTodos = useCallback(async () => {
    for (const registro of registros) {
      try {
        await atualizarStatus(registro.idProcesso, "IMPRIMINDO");
        const { data } = await api.get<DadosEtiquetaZpl>(
          `/impressao-etiqueta/etiqueta/${registro.idProcesso}`
        );
        const zpl = gerarZplEtiquetaProcesso(data);
        await enviarZpl(zpl, impressora ?? undefined);
        await atualizarStatus(registro.idProcesso, "IMPRESSO");
      } catch (error: any) {
        const msg = error?.response?.data?.message ?? error?.message ?? "Falha na impressão";
        toastErro(`${registro.idProcesso}: ${msg}`);
        await atualizarStatus(registro.idProcesso, "ERRO", msg);
      }
    }
    await buscarRegistros();
  }, [registros, atualizarStatus, impressora, buscarRegistros]);

  const testarImpressao = useCallback(async () => {
    const dadosTeste: DadosEtiquetaZpl = {
      empresa: "AW Administradora (TESTE)",
      placa: "TST0001",
      data: new Date().toLocaleDateString("pt-BR"),
      marcaModelo: "MARCA/MODELO TESTE",
      cor: "COR TESTE",
      localLacre: "LOCAL TESTE",
      chassi: "CHASSI00000000001",
    };
    try {
      const zpl = gerarZplEtiquetaProcesso(dadosTeste);
      await enviarZpl(zpl, impressora ?? undefined);
      toastSucesso("Etiqueta de teste enviada para a impressora.");
    } catch (error: any) {
      toastErro(error?.message ?? "Erro ao enviar teste para a impressora.");
    }
  }, [impressora]);

  useEffect(() => {
    buscarRegistros();
  }, [buscarRegistros]);

  const lerPlacaEObterDados = useCallback(
    async (imagemBase64: string): Promise<DadosEtiquetaZpl> => {
      const { data: { placa } } = await api.post<{ placa: string }>(
        "/impressao-etiqueta/leitura-placa",
        { imagemBase64 },
      );
      const { data: dadosEtiqueta } = await api.get<DadosEtiquetaZpl>(
        `/impressao-etiqueta/etiqueta-por-placa/${placa}`,
      );
      return dadosEtiqueta;
    },
    [],
  );

  const imprimirDadosEtiqueta = useCallback(
    async (dados: DadosEtiquetaZpl) => {
      const zpl = gerarZplEtiquetaProcesso(dados);
      await enviarZpl(zpl, impressora ?? undefined);
    },
    [impressora],
  );

  const importarLoteEImprimir = useCallback(
    async (itens: Array<{ placa: string; idProcesso: string }>): Promise<void> => {
      const { data: registros } = await api.post<IImpressaoEtiqueta[]>('/impressao-etiqueta/importar-lote', { itens });
      for (const registro of registros) {
        try {
          await atualizarStatus(registro.idProcesso, 'IMPRIMINDO');
          const { data: dadosEtiqueta } = await api.get<DadosEtiquetaZpl>(`/impressao-etiqueta/etiqueta/${registro.idProcesso}`);
          const zpl = gerarZplEtiquetaProcesso(dadosEtiqueta);
          await enviarZpl(zpl, impressora ?? undefined);
          await atualizarStatus(registro.idProcesso, 'IMPRESSO');
        } catch (error: any) {
          const msg = error?.response?.data?.message ?? error?.message ?? 'Falha na impressão';
          toastErro(`${registro.idProcesso}: ${msg}`);
          await atualizarStatus(registro.idProcesso, 'ERRO', msg);
        }
      }
      await buscarRegistros();
    },
    [atualizarStatus, impressora, buscarRegistros],
  );

  return {
    registros,
    loading,
    impressora,
    setImpressora,
    erroImpressora,
    impressoras,
    filtros,
    setFiltros,
    buscarRegistros,
    verificarImpressora,
    carregarImpressoras,
    atualizarStatus,
    reimprimir,
    imprimirTodos,
    testarImpressao,
    lerPlacaEObterDados,
    imprimirDadosEtiqueta,
    importarLoteEImprimir,
  };
}
